/**
 * 27/10/2026 — All on · All off for the containers on the board (user: "i want to be able to
 * switch all on off containers in the faster way"): one request for every container that isn't
 * already so, one refresh, one toast. All on asks first only when some of them would auto-send
 * real SMS today — the same answer as the rows' "sending automatically".
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import type { DropGenContainerSummary } from '../../../drop-generator.types';
import { useDropGeneratorMutations } from '../../../useDropGeneratorMutations';
import type { LiveAutoSend } from '../../shared/StateBadges';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The containers All on would switch, and how many of them would send real SMS by themselves. */
export function switchOnPlan(containers: DropGenContainerSummary[], autoSendOf: (id: string) => LiveAutoSend) {
  const ids = containers.filter((c) => !c.enabled).map((c) => c.id);
  return { ids, live: ids.filter((id) => autoSendOf(id) === 'sending').length };
}

export function useSwitchAll(planId: string, containers: DropGenContainerSummary[], autoSendOf: (id: string) => LiveAutoSend) {
  const { setContainersEnabled } = useDropGeneratorMutations();
  const toast = useToast();
  const [asking, setAsking] = useState<{ ids: string[]; live: number } | null>(null);
  const run = async (ids: string[], enabled: boolean) => {
    setAsking(null);
    if (ids.length === 0) return;
    try {
      await setContainersEnabled.mutateAsync({ planId, ids, enabled });
      toast.success(`${plural(ids.length, 'container')} switched ${enabled ? 'on' : 'off'}`);
    } catch (err) {
      toast.error(`Could not switch the containers ${enabled ? 'on' : 'off'}`, getApiErrorMessage(err));
    }
  };
  const allOn = () => {
    const plan = switchOnPlan(containers, autoSendOf);
    if (plan.live > 0) setAsking(plan);
    else void run(plan.ids, true);
  };
  return {
    allOn,
    allOff: () => void run(containers.filter((c) => c.enabled).map((c) => c.id), false),
    asking,
    confirm: () => asking && void run(asking.ids, true),
    cancel: () => setAsking(null),
    busy: setContainersEnabled.isPending,
  };
}
